import { createAjv } from "@jsonforms/core";
import { materialCells, materialRenderers } from "@jsonforms/material-renderers";
import { JsonForms } from "@jsonforms/react";
import ajvKeywords from "ajv-keywords";
import { useCallback, useMemo, useState } from "react";
import { isDeepEqual } from "../../../utils/commons";
import { VALIDATION_MODE } from "./constants";
import { customerRenderesHelper, getScopesofAllElements } from "./jsonFormsHelper";
import { LayoutTester } from "./testers";
// controllers
import { ConditionalSectionLayoutControl } from "../layouts/conditionalSectionLayout/ConditionalSectionLayout";
import { CustomGroupLayoutControl } from "../layouts/groupLayout/CustomGroupLayout";
import { HorizontalLayoutControl } from "../layouts/horizontalLayout/horizontalLayout";
import { InputRowControl } from "../layouts/inputRow/inputRow";
import { LayerLayoutControl } from "../layouts/layerLayout/LayerLayout";
import { SectionGroupLayoutControl } from "../layouts/sectionGroupLayout/SectionGroupLayout";
import { SimpleGroupControl } from "../layouts/simpleGroup/simpleGroup";
import { SimpleGroupPreviewCardsControl } from "../layouts/simpleGroupPreviewCards/simpleGroupPreviewCards";
import { VerticalLayoutControl } from "../layouts/verticalLayout/verticalLayout";

const computeDuplicateErrors = (schemaNode, dataNode, currentPath) => {
  if (!schemaNode || dataNode === undefined || dataNode === null) return [];
  const errors = [];

  if (
    schemaNode.type === "array" &&
    Array.isArray(schemaNode.uniqueItemProperties) &&
    Array.isArray(dataNode)
  ) {
    schemaNode.uniqueItemProperties.forEach((prop) => {
      const valueCounts = {};
      dataNode.forEach((item, i) => {
        const val = item?.[prop];
        if (val !== undefined && val !== "") {
          if (!valueCounts[val]) valueCounts[val] = [];
          valueCounts[val].push(i);
        }
      });
      Object.values(valueCounts).forEach((indices) => {
        if (indices.length > 1) {
          indices.forEach((i) => {
            errors.push({
              instancePath: `${currentPath}/${i}/${prop}`,
              schemaPath: "#/uniqueItemProperties",
              keyword: "uniqueItemProperties",
              params: { prop },
              message: "Duplicate parameter name",
            });
          });
        }
      });
    });
  }

  if (
    schemaNode.properties &&
    dataNode &&
    typeof dataNode === "object" &&
    !Array.isArray(dataNode)
  ) {
    Object.entries(schemaNode.properties).forEach(([key, propSchema]) => {
      errors.push(
        ...computeDuplicateErrors(propSchema, dataNode[key], `${currentPath}/${key}`)
      );
    });
  }

  if (schemaNode.type === "array" && schemaNode.items && Array.isArray(dataNode)) {
    dataNode.forEach((item, i) => {
      errors.push(
        ...computeDuplicateErrors(schemaNode.items, item, `${currentPath}/${i}`)
      );
    });
  }

  return errors;
};

// Deep-clone that keeps File/Blob instances by reference so they survive
// round-trips through state without being destroyed by JSON serialisation.
const deepClonePreservingFiles = (value) => {
  if (value === null || value === undefined) return value;
  if (value instanceof File || value instanceof Blob) return value;
  if (Array.isArray(value)) return value.map(deepClonePreservingFiles);
  if (typeof value === "object") {
    const result = {};
    for (const key of Object.keys(value)) {
      result[key] = deepClonePreservingFiles(value[key]);
    }
    return result;
  }
  return value;
};

export default function JsonFormsWrapper({
  jsonschema,
  uischema,
  data = {},
  onChange = () => null,
  readonly = false,
  config = {},
  otherData,
  additionalErrors = [],
}) {
  const [previousErrors, setPreviousErrors] = useState([]);
  const [prevValues, setPrevValues] = useState(data);
  const [internalAdditionalErrors, setInternalAdditionalErrors] = useState([]);
  const scopeAndCompsList = useMemo(
    () => getScopesofAllElements(uischema),
    [uischema]
  );

  const customRenderers = useMemo(
    () => customerRenderesHelper(scopeAndCompsList, otherData || data),
    [scopeAndCompsList, otherData, data]
  );

  const buildInternalAdditionalErrors = useCallback(
    (values, ajvErrors) => [
      ...ajvErrors,
      ...computeDuplicateErrors(jsonschema, values, ""),
    ],
    [jsonschema]
  );

  const onJsonFormsDataChange = (values, errors) => {
    const changed = !isDeepEqual(values, prevValues);
    const errorsChanged =
      previousErrors.length !== errors.length ||
      JSON.stringify(previousErrors) !== JSON.stringify(errors);

    if (changed || errorsChanged) {
      setInternalAdditionalErrors(buildInternalAdditionalErrors(values, errors));
      setPreviousErrors(errors);
      setPrevValues(deepClonePreservingFiles(values));
      onChange(values, errors);
    }
  };

  const renderers = useMemo(
    () => [
      ...materialRenderers,
      ...customRenderers,
      {
        tester: LayoutTester("VerticalLayout"),
        renderer: VerticalLayoutControl,
      },
      {
        tester: LayoutTester("HorizontalLayout"),
        renderer: HorizontalLayoutControl,
      },
      {
        tester: LayoutTester("SimpleGroup"),
        renderer: SimpleGroupControl,
      },
      {
        tester: LayoutTester("simpleGroupPreviewCards"),
        renderer: SimpleGroupPreviewCardsControl,
      },
      {
        tester: LayoutTester("CustomGroup"),
        renderer: CustomGroupLayoutControl,
      },
      {
        tester: LayoutTester("InputRow"),
        renderer: InputRowControl,
      },
      {
        tester: LayoutTester("ConditionalSectionLayout"),
        renderer: ConditionalSectionLayoutControl,
      },
      {
        tester: LayoutTester("LayerLayout"),
        renderer: LayerLayoutControl,
      },
      {
        tester: LayoutTester("SectionGroupLayout"),
        renderer: SectionGroupLayoutControl,
      },
    ],
    [customRenderers]
  );
  const handleDefaultsAjv = useMemo(() => {
    const ajv = createAjv({ useDefaults: true });
    ajvKeywords(ajv, ["uniqueItemProperties"]);
    return ajv;
  }, []);
  const safeData = useMemo(() => deepClonePreservingFiles(data), [data]);

  return (
    <JsonForms
      readonly={readonly}
      schema={jsonschema}
      uischema={uischema}
      data={safeData}
      renderers={renderers}
      cells={materialCells}
      onChange={({ errors, data }) => onJsonFormsDataChange(data, errors)}
      ajv={handleDefaultsAjv}
      config={{ ...config, readonly }}
      validationMode={
        config?.showValidation ? VALIDATION_MODE.SHOW : VALIDATION_MODE.HIDE
      }
      additionalErrors={[
        ...additionalErrors,
        // In ValidateAndShow mode, schema validation already shows AJV errors;
        // only pass through custom field-level duplicate errors to avoid doubling.
        ...(config?.showValidation
          ? internalAdditionalErrors.filter(
              (e) => e.schemaPath === "#/uniqueItemProperties"
            )
          : internalAdditionalErrors),
      ]}
    />
  );
}
